import { AfterViewInit, Component, ElementRef, Input, QueryList, ViewChild, ViewChildren } from "@angular/core";
import { AbstractControl, UntypedFormArray, UntypedFormGroup } from "@angular/forms";
import { MatAccordion, MatExpansionPanel } from "@angular/material/expansion";

import { Observable, of, throwError } from "rxjs";
import { delay, filter, first, mapTo, tap } from "rxjs/operators";
import scrollIntoView from "scroll-into-view-if-needed";

import { DffSimpelDialog } from "@dffedb/ui";
import { EntitetStatus } from "@dffedb/util";

import { NoteBruger, NoteHenvendelsestype, NoteId, NoteOmraade, NoteOpgavestyringStatus, opretTomNote } from "../model/note.model";
import { NoteFormBuilder } from "../shared/note-form-builder";

type FormGroupFunc = (formGroup: UntypedFormGroup) => Observable<UntypedFormGroup>;

@Component({
    selector: "note-liste",
    templateUrl: "./note-liste.component.html",
    styleUrls: ["./note-liste.component.scss"]
})
export class NoteListeComponent implements AfterViewInit {
    public get synligeNoter(): AbstractControl[] {
        return (this.formArray && this.formArray.controls.filter((c) => c.get("status").value !== EntitetStatus.Slettet)) || [];
    }

    @Input() public formArray: UntypedFormArray;
    @Input() public omraader: NoteOmraade[] = [];
    @Input() public henvendelsestyper: NoteHenvendelsestype[] = [];
    @Input() public opgavestyringStatusser: NoteOpgavestyringStatus[] = [];
    @Input() public brugere: NoteBruger[] = [];
    @Input() public defaultOmraadeId: number = null;
    @Input() public aktivNoteId: number = null;

    @Input() public hent: FormGroupFunc;
    @Input() public gem: FormGroupFunc;
    @Input() public slet: FormGroupFunc;

    @ViewChildren(MatExpansionPanel) public expansionPanels: QueryList<MatExpansionPanel>;
    @ViewChildren(MatExpansionPanel, { read: ElementRef }) public expansionPanelElements: QueryList<ElementRef>;
    @ViewChild(MatAccordion) public accordion: MatAccordion;

    @Input() public visOpretKnap = true;
    @Input() public autoGem = false;

    constructor(private readonly dialog: DffSimpelDialog) {}

    public async ngAfterViewInit(): Promise<void> {
        await this.openPanelWithNoteIdAndScrollIntoView(this.aktivNoteId);
    }

    public hentOmraadeTekst(note: NoteId): string {
        const omraade = this.omraader && this.omraader.find((o) => o.key === note.omraadeId);
        return omraade && omraade.value;
    }

    public onOpretClick(): void {
        const nyNote = opretTomNote(this.defaultOmraadeId);
        const nyNoteFormGroup = NoteFormBuilder.noteToFormGroup(
            nyNote,
            this.omraader,
            this.henvendelsestyper,
            this.opgavestyringStatusser,
            this.brugere
        );
        this.formArray.insert(0, nyNoteFormGroup);
        setTimeout(() => this.expansionPanels.first.open());
    }

    public onPanelOpened(noteFormGroup: UntypedFormGroup): void {
        if (!noteFormGroup.value.fullyLoaded) {
            setTimeout(() => {
                const hent = this.convertException(this.hent || this.dummyFormGroupFunc);
                hent(noteFormGroup)
                    .pipe(first())
                    .subscribe((hentetNoteFormGroup: UntypedFormGroup) => {
                        NoteFormBuilder.setControls(noteFormGroup, hentetNoteFormGroup.controls);
                    });
            }, 100);
        }
    }

    public async onPanelClosed(noteFormGroup: UntypedFormGroup): Promise<void> {
        if (noteFormGroup.value.noteId === 0 && noteFormGroup.untouched) {
            await this.sletNote(noteFormGroup);
        }
    }

    public async onGemClick(event: Event, noteFormGroup: UntypedFormGroup): Promise<void> {
        event.stopPropagation();
        await this.gemNote(noteFormGroup);
        this.collapseAll();
    }

    public async onSletClick(event: Event, noteFormGroup: UntypedFormGroup): Promise<void> {
        event.stopPropagation();
        if (await this.dialog.openJaNej("Slet", "Ønsker du at slettet noten?")) {
            this.sletNote(noteFormGroup);
        }
    }

    public skalGemKnapVises(panel: MatExpansionPanel, note: UntypedFormGroup): boolean {
        if (this.autoGem) {
            return false;
        }
        return panel.expanded || note.dirty;
    }

    public skalSletKnapVises(panel: MatExpansionPanel): boolean {
        return panel.expanded;
    }

    private dummyFormGroupFunc: FormGroupFunc = (formGroup: UntypedFormGroup) => of(formGroup);

    private gemNote(noteFormGroup: UntypedFormGroup): Promise<UntypedFormGroup> {
        const gem = this.convertException(this.gem || this.dummyFormGroupFunc);
        return gem(noteFormGroup)
            .pipe(
                first(),
                tap((gemtNoteFormGroup: UntypedFormGroup) => {
                    // Påtryk ændrede værdier
                    NoteFormBuilder.setControls(noteFormGroup, gemtNoteFormGroup.controls);
                    noteFormGroup.markAsPristine();
                    // Har gem() ikke tildelt en ny noteId? Så sætter vi en negativ værdi for at indikere at det er en memory-only note.
                    if (noteFormGroup.value.noteId === 0) {
                        noteFormGroup.get("noteId").setValue(this.genererNegativNoteId());
                    }
                })
            )
            .toPromise();
    }

    private sletNote(noteFormGroup: UntypedFormGroup): Promise<UntypedFormGroup> {
        const slet =
            noteFormGroup.get("noteId").value === 0
                ? this.convertException(this.dummyFormGroupFunc)
                : this.convertException(this.slet || this.dummyFormGroupFunc);

        return slet(noteFormGroup)
            .pipe(
                first(),
                tap((slettetNoteFormGroup: UntypedFormGroup) => {
                    // Sæt status til Slettet, så noten bliver usynlig for brugeren.
                    noteFormGroup.get("status").setValue(EntitetStatus.Slettet);

                    // Vi kan ikke altid tillade os at fjerne noten fra listen,
                    // da vi i nogle situationer samler op på nye / redigerede / slettede noter.
                    // Dette er f.eks. tilfældet i forbindelse med Servicerapporter.
                    // Hvis gem() returnerer 'null', betyder det dog at den kaldte rutine har valgt, vi må slette noten fra listen.
                    // Hvis noteId <= 0, er det en memory-only note, og vi kan ligeledes slette den fra listen.
                    const fjernFraListen = !slettetNoteFormGroup || slettetNoteFormGroup.value.noteId <= 0;
                    if (fjernFraListen) {
                        const index = this.formArray.controls.indexOf(noteFormGroup);
                        this.formArray.removeAt(index);
                    }
                })
            )
            .toPromise();
    }

    private collapseAll(): void {
        this.expansionPanels.forEach((panel) => {
            panel.disabled = false;
            panel.close();
        });
    }

    private genererNegativNoteId(): number {
        const result = this.formArray.controls.reduce((previous, noteFormGroup) => Math.min(previous, noteFormGroup.value.noteId), 0) - 1;
        return result;
    }

    private convertException(func: FormGroupFunc): FormGroupFunc {
        // Konverterer eventuelle exceptions til fejl, som kan håndteres i rxjs.
        return (formGroup: UntypedFormGroup) => {
            try {
                return func(formGroup);
            } catch (error) {
                return throwError(error);
            }
        };
    }

    private async openPanelWithNoteIdAndScrollIntoView(noteId: number): Promise<void> {
        const elementRef = await this.openPanelWithNoteId(noteId);
        if (elementRef) {
            const scrollMarginTop = elementRef.nativeElement.querySelector(".scroll-margin-top");
            scrollIntoView(scrollMarginTop, {
                behavior: "smooth",
                block: "start",
                inline: "nearest",
                scrollMode: "always"
            });
        }
    }

    private async openPanelWithNoteId(noteId: number): Promise<ElementRef> {
        const index = this.expansionPanelElements.toArray().findIndex((e) => +e.nativeElement.dataset.noteid === noteId);
        if (index === -1) {
            return Promise.resolve(null);
        }
        const expansionPanel = this.expansionPanels.toArray()[index];
        const elementRef = this.expansionPanelElements.toArray()[index];
        const noteFormGroup = this.formArray.controls[index];
        setTimeout(() => expansionPanel.open());
        return await noteFormGroup.valueChanges
            .pipe(
                filter((value) => !!value.fullyLoaded),
                first(),
                mapTo(elementRef),
                delay(0)
            )
            .toPromise();
    }
}
